import { useContext, useRef, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

import { Context } from './store/Context'
import { detectDevice } from './utils/deviceDetect'
import wordmark from './images/shangyu-wordmark.png'
import fishTank from './images/fish-tank.png'
import fishDex from './images/fish-dex.png'
import fishFeed from './images/fish-feed.png'
import fishLog from './images/fish-log.png'
import SiteHeader from './SiteHeader'
import './Home.css'

const EASE = [0.16, 1, 0.3, 1]

/* ---------- platform glyphs ---------- */
function AppleIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M16.36 12.9c.02 2.5 2.2 3.33 2.22 3.34-.02.06-.35 1.2-1.15 2.37-.7 1.02-1.42 2.03-2.56 2.05-1.12.02-1.48-.66-2.76-.66-1.28 0-1.68.64-2.74.68-1.1.04-1.94-1.1-2.64-2.11-1.44-2.08-2.54-5.87-1.06-8.43.73-1.27 2.05-2.08 3.48-2.1 1.08-.02 2.1.73 2.76.73.66 0 1.9-.9 3.2-.77.55.02 2.08.22 3.07 1.67-.08.05-1.83 1.07-1.81 3.19M14.3 5.2c.58-.7.97-1.68.86-2.66-.84.03-1.85.56-2.45 1.26-.53.62-1 1.61-.88 2.57.94.07 1.9-.48 2.47-1.17" />
        </svg>
    )
}
function AndroidIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.6 9.48l1.84-3.18a.4.4 0 00-.15-.55.4.4 0 00-.54.15l-1.87 3.23A11.4 11.4 0 0012 8.1c-1.7 0-3.32.36-4.78 1.03L5.35 5.9a.4.4 0 00-.54-.15.4.4 0 00-.15.55L6.5 9.48A10.8 10.8 0 001 18.15h22a10.8 10.8 0 00-5.4-8.67M7 15.25a1.06 1.06 0 110-2.12 1.06 1.06 0 010 2.12m10 0a1.06 1.06 0 110-2.12 1.06 1.06 0 010 2.12" />
        </svg>
    )
}

/* ---------- reveal helpers ----------
   Content is visible by DEFAULT (opacity stays 1). Motion only slides it into
   place on scroll, so nothing ever ships blank in a headless renderer, crawler,
   social-preview bot, or a hidden tab where scroll reveals never fire. */
function Reveal({ children, className, y = 28, x = 0, delay = 0, amount = 0.2 }) {
    const reduce = useReducedMotion()
    if (reduce) return <div className={className}>{children}</div>
    return (
        <motion.div
            className={className}
            initial={{ y, x }}
            whileInView={{ y: 0, x: 0 }}
            viewport={{ once: true, amount }}
            transition={{ duration: 0.8, ease: EASE, delay }}
        >
            {children}
        </motion.div>
    )
}

/* 青柠荧光笔划过：高亮从左到右扫出。仅动 background-size，文字始终可读 */
function MarkSwipe({ children, delay = 0.35 }) {
    const reduce = useReducedMotion()
    if (reduce) return <span className="sy-mark">{children}</span>
    return (
        <motion.span
            className="sy-mark"
            initial={{ backgroundSize: '0% 42%' }}
            whileInView={{ backgroundSize: '100% 42%' }}
            viewport={{ once: true, amount: 0.9 }}
            transition={{ duration: 0.65, ease: EASE, delay }}
        >
            {children}
        </motion.span>
    )
}

function DownloadButtons({ variant = 'light' }) {
    const { state } = useContext(Context)
    const ghost = variant === 'dark' ? 'sy-btn--on-dark' : 'sy-btn--ghost'
    return (
        <>
            <a
                className={`sy-btn ${variant === 'dark' ? 'sy-btn--lime' : 'sy-btn--primary'}`}
                href={state.iosLink}
                target="_blank"
                rel="noopener noreferrer"
            >
                <AppleIcon />
                App Store 下载
            </a>
            <div className="sy-download-android">
                <a
                    className={`sy-btn ${ghost}`}
                    href={state.androidLink}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <AndroidIcon />
                    Android 下载
                </a>
                <a
                    className={`sy-download-apk${variant === 'dark' ? ' sy-download-apk--on-dark' : ''}`}
                    href={state.androidLink}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    APK 下载
                </a>
            </div>
        </>
    )
}

function Hero() {
    const { state } = useContext(Context)
    const reduce = useReducedMotion()

    const container = {
        hidden: {},
        show: { transition: { staggerChildren: 0.12, delayChildren: 0.05 } },
    }
    const item = {
        hidden: reduce ? {} : { opacity: 0, y: 24 },
        show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
    }

    return (
        <section className="sy-hero">
            <div className="sy-container sy-hero__grid">
                <motion.div variants={container} initial="hidden" animate="show">
                    <motion.h1 variants={item} className="sy-display sy-hero__title">
                        你的专属<MarkSwipe delay={0.6}>钓鱼</MarkSwipe>助手
                    </motion.h1>
                    <motion.p variants={item} className="sy-hero__sub">
                        {state.coverText}
                    </motion.p>
                    <motion.div variants={item} className="sy-hero__cta">
                        <DownloadButtons />
                    </motion.div>
                    <motion.p variants={item} className="sy-hero__note">
                        iOS · Android 双端支持，免费下载
                    </motion.p>
                </motion.div>

                <div className="sy-hero__media">
                    <motion.img
                        src={state.coverImage}
                        alt="上渔 App 界面：钓鱼地图与鱼类详情"
                        initial={reduce ? false : { opacity: 0, y: 40, scale: 0.96 }}
                        animate={reduce ? {} : { opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
                        {...(reduce
                            ? {}
                            : {
                                  whileHover: { y: -8 },
                              })}
                    />
                </div>
            </div>
        </section>
    )
}

const TOOL_STEPS = [
    {
        label: '定位当前水域',
        icon: (
            <>
                <path d="M12 21s-7-5.2-7-11a7 7 0 1 1 14 0c0 5.8-7 11-7 11z" />
                <circle cx="12" cy="10" r="2.6" />
            </>
        ),
    },
    {
        label: '结合实时天气',
        icon: (
            <>
                <path d="M7 5.2V3.4M3.4 8.8H5.2M4.5 5.9l1.2 1.2" />
                <path d="M10.8 8.6a3.4 3.4 0 1 0-4.4 4" />
                <path d="M16 20H9.5a4 4 0 0 1-.4-8A5.5 5.5 0 0 1 19.8 13 3.6 3.6 0 0 1 16 20z" />
            </>
        ),
    },
    {
        label: '生成今日策略',
        icon: (
            <>
                <path d="M11 4.5l1.6 4 4 1.6-4 1.6-1.6 4-1.6-4-4-1.6 4-1.6 1.6-4z" />
                <path d="M18.6 14.6l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8.8-2z" />
            </>
        ),
    },
]

function ToolBand() {
    const reduce = useReducedMotion()
    // 三步是真实的流程，入场按顺序逐个弹入；只动 transform，不碰透明度
    const stepsContainer = {
        show: { transition: { staggerChildren: 0.16, delayChildren: 0.1 } },
    }
    const stepItem = reduce
        ? {}
        : {
              hidden: { y: 18, scale: 0.9 },
              show: { y: 0, scale: 1, transition: { duration: 0.5, ease: EASE } },
          }
    return (
        <section className="sy-tool">
            <div className="sy-container sy-tool__inner">
                <Reveal>
                    <h2 className="sy-display sy-tool__title">
                        今天<span className="sy-hl-lime">怎么钓</span>？
                    </h2>
                </Reveal>
                <Reveal delay={0.08}>
                    <p className="sy-tool__text">
                        基于当前位置与天气，一键生成属于今天的钓鱼策略。
                    </p>
                </Reveal>
                <motion.ol
                    className="sy-tool__steps"
                    variants={stepsContainer}
                    initial={reduce ? false : 'hidden'}
                    whileInView="show"
                    viewport={{ once: true, amount: 0.6 }}
                >
                    {TOOL_STEPS.map((step, i) => (
                        <motion.li key={step.label} variants={stepItem}>
                            {i > 0 && (
                                <svg className="sy-tool__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                                    <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                            <span className="sy-tool__chip">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    {step.icon}
                                </svg>
                                {step.label}
                            </span>
                        </motion.li>
                    ))}
                </motion.ol>
                <Reveal delay={0.2}>
                    <a className="sy-btn sy-btn--lime" href="/tool.html">
                        开始体验
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                            <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </a>
                </Reveal>
            </div>
        </section>
    )
}

function Feature({ item, index }) {
    const reversed = index % 2 === 1
    const tinted = index % 2 === 1
    const mediaX = reversed ? 44 : -44
    return (
        <section className={`sy-band ${tinted ? 'sy-band--tint' : ''}`}>
            <div className={`sy-container sy-feature ${reversed ? 'sy-feature--rev' : ''}`}>
                <Reveal className="sy-feature__media" x={mediaX} amount={0.25}>
                    {item.frame ? (
                        <div className="sy-phone-frame">
                            <img src={item.image} alt={`${item.title}界面`} loading="lazy" />
                        </div>
                    ) : (
                        <img src={item.image} alt={`${item.title}功能界面`} loading="lazy" />
                    )}
                </Reveal>
                <Reveal amount={0.25} delay={0.08}>
                    <div className="sy-feature__body">
                        <span className="sy-feature__kicker">{item.kicker}</span>
                        <h3 className="sy-display sy-feature__title">{item.title}</h3>
                        <p className="sy-feature__lead">{item.lead}</p>
                        <p className="sy-feature__text">{item.text}</p>
                        {item.cta && (
                            <a className="sy-btn sy-btn--primary sy-feature__cta" href={item.cta.href}>
                                {item.cta.label}
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                                    <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </a>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    )
}

/* ==========================================================================
   双世界切换：实钓功能 vs 电子鱼缸
   ========================================================================== */

/* 鱼缸分支：真实界面截图 + 图文交替行，与实钓分支同一套版式 */
const TANK_SECTIONS = [
    {
        title: '电子鱼缸',
        lead: '把每一次渔获，变成一条数字鱼。',
        text: '电子鱼缸是上渔的渔获记录与游戏化展示系统：把现实中钓到的鱼上传进鱼缸，每一次渔获都变成可收藏、可互动、可成长的数字鱼。',
        image: fishTank,
    },
    {
        title: '图鉴系统',
        lead: '每解锁一种鱼，都有一张资料卡。',
        text: '图鉴展示你已经解锁和收集过的鱼种，每一种鱼都有独立的资料卡：名称、稀有度、分布区域、生活习性、适合季节、常见钓法，一应俱全。',
        image: fishDex,
    },
    {
        title: '喂食系统',
        lead: '鱼有偏好，喂对了才有反应。',
        text: '鱼缸中的鱼不是静态展示物，而是有状态、有偏好的数字生物。不同鱼类有不同的食物喜好，根据它们的习性挑选合适的食物投喂。',
        image: fishFeed,
    },
    {
        title: '日志系统',
        lead: '记下每一次出钓，攒成你的钓鱼数据库。',
        text: '日志系统记录每一次真实的钓鱼经历，渔获数据随时回顾，日积月累，形成一份属于你自己的个人钓鱼数据库。',
        image: fishLog,
    },
]

function TankWorld() {
    return TANK_SECTIONS.map((item, index) => (
        <Feature key={item.title} item={item} index={index} />
    ))
}

const WORLD_TABS = [
    {
        key: 'field',
        title: '去水边实钓',
        text: '水域标点、AI 实景钓点推荐、智能选装备，出发前后都有数。',
        go: '看实钓功能',
    },
    {
        key: 'tank',
        title: '把渔获养进鱼缸',
        text: '图鉴、喂食、日志，每一条渔获都留得下来。',
        go: '看电子鱼缸',
    },
]

function FeatureWorlds() {
    const { state } = useContext(Context)
    const [world, setWorld] = useState(null)
    const reduce = useReducedMotion()
    const tabRefs = useRef([])

    const onTabKeyDown = (e, index) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
        e.preventDefault()
        tabRefs.current[index === 0 ? 1 : 0]?.focus()
    }

    const panelMotion = reduce
        ? {}
        : {
              initial: { opacity: 0, y: 24 },
              animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: EASE } },
              exit: { opacity: 0, y: -12, transition: { duration: 0.25, ease: EASE } },
          }

    return (
        <section className="sy-worlds">
            <div className="sy-container">
                <Reveal className="sy-section-head sy-worlds__head">
                    <h2 className="sy-display sy-section-head__title">
                        <span className="sy-worlds__title-line">上渔帮你找到鱼，</span>
                        <span className="sy-worlds__title-line">
                            也帮你<MarkSwipe>留住鱼</MarkSwipe>
                        </span>
                    </h2>
                    <p className="sy-section-head__sub">
                        实钓功能帮你科学出钓，电子鱼缸帮你收藏每一条渔获。选择一个入口开始体验。
                    </p>
                </Reveal>

                <Reveal amount={0.3}>
                    <div
                        role="tablist"
                        aria-label="选择功能板块"
                        className="sy-worlds__cards"
                        data-chosen={world ? 'yes' : 'no'}
                    >
                        {WORLD_TABS.map((tab, i) => (
                            <button
                                key={tab.key}
                                ref={(el) => (tabRefs.current[i] = el)}
                                role="tab"
                                id={`sy-worldtab-${tab.key}`}
                                aria-selected={world === tab.key}
                                aria-controls={`sy-worldpanel-${tab.key}`}
                                tabIndex={world ? (world === tab.key ? 0 : -1) : i === 0 ? 0 : -1}
                                className={`sy-world-card sy-world-card--${tab.key}`}
                                onClick={() => setWorld(tab.key)}
                                onKeyDown={(e) => onTabKeyDown(e, i)}
                            >
                                <span className="sy-display sy-world-card__title">{tab.title}</span>
                                <span className="sy-world-card__text">{tab.text}</span>
                                <span className="sy-world-card__go">
                                    {world === tab.key ? '正在看这边' : tab.go}
                                    {world === tab.key ? (
                                        <svg className="sy-world-card__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" aria-hidden="true">
                                            <path d="M4.5 12.5l5 5L19.5 7" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
                                            <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </span>
                            </button>
                        ))}
                    </div>
                </Reveal>
            </div>

            <AnimatePresence mode="wait" initial={false}>
                {world === 'field' && (
                    <motion.div
                        key="field"
                        id="sy-worldpanel-field"
                        role="tabpanel"
                        aria-labelledby="sy-worldtab-field"
                        {...panelMotion}
                    >
                        {state.sectionList.map((item, index) => (
                            <Feature key={item.title} item={item} index={index} />
                        ))}
                    </motion.div>
                )}
                {world === 'tank' && (
                    <motion.div
                        key="tank"
                        id="sy-worldpanel-tank"
                        role="tabpanel"
                        aria-labelledby="sy-worldtab-tank"
                        {...panelMotion}
                    >
                        <TankWorld />
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    )
}

function ClosingCTA() {
    return (
        <section className="sy-cta">
            <div className="sy-container sy-cta__inner">
                <Reveal>
                    <h2 className="sy-display sy-cta__title">
                        你的下一杆，<span className="sy-hl-lime">就在这里</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.08}>
                    <p className="sy-cta__text">
                        免费下载上渔，找到鱼、看懂鱼、钓到鱼。
                    </p>
                </Reveal>
                <Reveal delay={0.14}>
                    <div className="sy-cta__row">
                        <DownloadButtons variant="dark" />
                    </div>
                </Reveal>
            </div>
        </section>
    )
}

function Footer() {
    const { state } = useContext(Context)
    const tel = state.supportPhone.replace(/\s/g, '')
    return (
        <footer className="sy-footer">
            <div className="sy-container sy-footer__inner">
                <div className="sy-footer__brand">
                    <img className="sy-footer__logo" src={state.appLogo} alt="" />
                    <img className="sy-footer__wordmark" src={wordmark} alt={state.appName} />
                </div>
                <div className="sy-footer__meta">
                    <span>
                        支持电话：<a href={`tel:${tel}`}>{state.supportPhone}</a>
                    </span>
                    <span>
                        <a href="/privacy">隐私政策</a>
                    </span>
                    <span>
                        {state.appName} © {new Date().getFullYear()}
                    </span>
                </div>
            </div>
        </footer>
    )
}

function Home() {
    // eslint-disable-next-line no-unused-vars
    const device = detectDevice()

    return (
        <div className="sy-home">
            <SiteHeader home />
            <main>
                <Hero />
                <ToolBand />
                <FeatureWorlds />
                <ClosingCTA />
            </main>
            <Footer />
        </div>
    )
}

export default Home
